/**
 * Lighting rig: directional sun light with soft (PCF) shadows + ambient
 * fill. Purely visual — GOAL.md visual bar calls for "properly lit ...
 * soft shadows"; this has no bearing on any LOS calculation, which is
 * raypath's analytical raster (hard constraint #1).
 *
 * Sun/ambient colours derive from scene/atmosphere.ts's single palette
 * (issue #60), the same source scene/sky.ts's horizon/zenith gradient and
 * scene/fade.ts's fog colour use, so sunlight can never disagree with the
 * sky/haze tint. `sunLightColorHex`/`ambientLightColorHex` are pulled out
 * as pure functions so that mapping is unit-testable without a
 * WebGLRenderer (setupLighting itself needs a real GL context, which the
 * unit test environment doesn't provide).
 */
import { AmbientLight, DirectionalLight, PCFSoftShadowMap, Scene, WebGLRenderer } from 'three';
import { ATMOSPHERE_PALETTE, rgbToHex, type AtmospherePalette } from './atmosphere';
import type { RGB } from './colorGrade';

export interface LightingRig {
  sun: DirectionalLight;
  ambient: AmbientLight;
}

/** Global ambient-fill intensity (the flat sky-bounce term). The sky-tinted
 * palette colour makes this cool, which is why sun-shaded building walls read
 * blue without the building-only warm fill below (issue #154). */
export const AMBIENT_INTENSITY = 0.55;

/**
 * Building-only warm ambient fill (issue #154). The scene AmbientLight is
 * sky-tinted (cool), so a sun-shaded wall — lit by ambient alone — came out
 * slate-blue, swamping the per-building wall tint (#150). We add a warm term
 * *only to building materials* (via a one-time shader patch, see
 * applyBuildingFill in buildings.ts), proportional to each fragment's own
 * albedo. Because it is albedo-proportional it stays the wall's own hue (a warm
 * stone lifts warm, a slate wall barely lifts) and can never raise a dark
 * timber/metal wall toward grey — it only shifts the *hue* of the shadow, not
 * its value. Terrain is untouched (terrainPalette e2e must stay green): a plain
 * three.js light can't be limited to buildings (light.layers is tested against
 * the camera, not per object), which is why this is a material-side term.
 */
export const BUILDING_FILL_COLOR: RGB = { r: 1.0, g: 0.83, b: 0.62 };
/** Coefficient on the albedo-proportional warm term. Tuned so it just cancels
 * the cool ambient bias on a shaded wall (see shadedWallAmbient); bounded well
 * below 1 so the shadow lift stays a hue shift, never a wash-out. */
export const BUILDING_FILL_STRENGTH = 0.32;

/**
 * The building fill as a premultiplied linear RGB triple, ready to hand to the
 * shader uniform (colour x strength). Kept here, at the palette's side, so the
 * value the shader adds and the value the unit tests reason about are the same.
 */
export function buildingFillPremultiplied(): RGB {
  return {
    r: BUILDING_FILL_COLOR.r * BUILDING_FILL_STRENGTH,
    g: BUILDING_FILL_COLOR.g * BUILDING_FILL_STRENGTH,
    b: BUILDING_FILL_COLOR.b * BUILDING_FILL_STRENGTH,
  };
}

/**
 * The effective ambient irradiance on a sun-shaded building wall: the flat
 * AmbientLight term plus the warm building fill. Pure (no three.js), so the
 * balance that keeps shaded walls warm rather than slate-blue (issue #154) is
 * unit-testable without a GL context. The shader adds `fill * albedo` on top of
 * the lit result, i.e. the wall's shaded appearance is `albedo * (ambient +
 * fill)` — so this returns `ambient + fill`, the combined ambient the wall's
 * own colour is multiplied by.
 */
export function shadedWallAmbient(palette: AtmospherePalette = ATMOSPHERE_PALETTE): RGB {
  const a = palette.ambient;
  const fill = buildingFillPremultiplied();
  return {
    r: a.r * AMBIENT_INTENSITY + fill.r,
    g: a.g * AMBIENT_INTENSITY + fill.g,
    b: a.b * AMBIENT_INTENSITY + fill.b,
  };
}

export interface LightingOptions {
  /** Whether to render real-time sun shadows. Off on the low quality tier
   * (issue #17) — the shadow map pass over ~180 tiles + hundreds of buildings
   * is a top GPU cost on integrated GPUs. */
  shadows?: boolean;
  /** Sun shadow map resolution (only used when shadows are on). */
  shadowMapSize?: number;
}

export function sunLightColorHex(palette: AtmospherePalette = ATMOSPHERE_PALETTE): number {
  return rgbToHex(palette.sun);
}

export function ambientLightColorHex(palette: AtmospherePalette = ATMOSPHERE_PALETTE): number {
  return rgbToHex(palette.ambient);
}

export function setupLighting(scene: Scene, renderer: WebGLRenderer, options: LightingOptions = {}): LightingRig {
  const shadows = options.shadows ?? true;
  const shadowMapSize = options.shadowMapSize ?? 2048;

  renderer.shadowMap.enabled = shadows;
  renderer.shadowMap.type = PCFSoftShadowMap;

  const sun = new DirectionalLight(sunLightColorHex(), 2.4);
  sun.position.set(-1500, 1800, 900); // roughly matches viewpoints.json's default bearing (347°, NNW)
  sun.castShadow = shadows;
  sun.shadow.mapSize.set(shadowMapSize, shadowMapSize);
  sun.shadow.camera.near = 10;
  sun.shadow.camera.far = 8000;
  sun.shadow.camera.left = -3000;
  sun.shadow.camera.right = 3000;
  sun.shadow.camera.top = 3000;
  sun.shadow.camera.bottom = -3000;
  sun.shadow.bias = -0.0008;

  const ambient = new AmbientLight(ambientLightColorHex(), AMBIENT_INTENSITY);

  scene.add(sun, sun.target, ambient);
  return { sun, ambient };
}
